class KitBuilder {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.enableDiscount = container.dataset.enableDiscount === 'true';
    this.freeMode = container.dataset.freeMode === 'true';
    this.freeMin = parseInt(container.dataset.freeMin) || 3;

    this.selectedItems = [];
    this.currentSize = this.freeMode ? Infinity : (parseInt(container.dataset.defaultSize) || 3);
    this.currentDiscount = parseFloat(container.dataset.defaultDiscount) || 0;

    this.slotsEl = container.querySelector(`#kits-slots-${this.sectionId}`);
    this.noticeEl = container.querySelector(`#kits-notice-${this.sectionId}`);
    this.actionBtn = container.querySelector(`#kits-action-${this.sectionId}`);
    this.addToCartText = this.actionBtn ? this.actionBtn.dataset.addText : 'ADICIONAR AO CARRINHO';

    this.popupEl = document.querySelector(`#kits-popup-${this.sectionId}`);
    this.popupBtn = document.querySelector(`#kits-popup-btn-${this.sectionId}`);

    this._init();
  }

  _init() {
    // Read add-to-cart text from schema (stored in data attribute)
    const atcText = this.container.dataset.addToCartText;
    if (atcText) this.addToCartText = atcText;

    // Tabs
    this.container.querySelectorAll('.kits__tab').forEach(tab => {
      tab.addEventListener('click', () => this._handleTab(tab));
    });

    // ADD buttons
    this.container.querySelectorAll('.kits__add-btn').forEach(btn => {
      btn.addEventListener('click', () => this._handleAdd(btn.closest('.kits__card')));
    });

    // Swatch dots + image update on variant change
    this.container.querySelectorAll('.kits__card').forEach(card => {
      this._updateSwatchDot(card);
      this._updateCardImage(card);
      const select = card.querySelector('.kits__variant-select');
      if (select && select.tagName === 'SELECT') {
        select.addEventListener('change', () => {
          this._updateSwatchDot(card);
          this._updateCardImage(card);
        });
      }
    });

    // Hover images
    this.container.querySelectorAll('.kits__card-img[data-hover]').forEach(img => {
      const hover = img.dataset.hover;
      img.closest('.kits__card-image').style.setProperty('--hover-bg', `url(${hover})`);
      img.closest('.kits__card-image').style.cssText += `--hover-bg: url(${hover});`;
      img.parentElement.style.setProperty('background-image', `url(${hover})`);
      img.parentElement.addEventListener('mouseenter', () => { img.parentElement.style.backgroundImage = `url(${hover})`; img.parentElement.style.backgroundSize = 'cover'; img.parentElement.style.backgroundPosition = 'center'; });
    });

    // Size options
    this.container.querySelectorAll(`input[name="kit-size-${this.sectionId}"]`).forEach(radio => {
      radio.addEventListener('change', () => this._handleSizeChange(radio));
    });

    // Action button
    if (this.actionBtn) {
      this.actionBtn.addEventListener('click', () => this._addToCart());
    }

    // Popup button (mirrors action btn)
    if (this.popupBtn) {
      this.popupBtn.addEventListener('click', () => this._addToCart());
    }

    // Scroll-triggered popup
    if (this.popupEl) {
      this._initPopup();
    }

    // Initialize from first checked radio (may override constructor defaults)
    const firstRadio = this.container.querySelector(`input[name="kit-size-${this.sectionId}"]:checked`);
    if (firstRadio) {
      this.freeMode = firstRadio.dataset.free === 'true';
      this.currentDiscount = parseFloat(firstRadio.dataset.discount || 0);
      if (this.freeMode) {
        this.currentSize = Infinity;
        this.freeMin = parseInt(firstRadio.dataset.freeMin) || 3;
      } else {
        this.currentSize = parseInt(firstRadio.value) || 3;
      }
    }

    this._renderSlots();
    this._updateActionBtn();
    this._updatePopup();
  }

  _handleTab(tab) {
    this.container.querySelectorAll('.kits__tab').forEach(t => t.classList.remove('kits__tab--active'));
    tab.classList.add('kits__tab--active');
    const target = tab.dataset.collection;
    this.container.querySelectorAll('.kits__grid').forEach(grid => {
      grid.classList.toggle('kits__grid--hidden', grid.id !== target);
    });
  }

  _handleAdd(card) {
    if (!card) return;
    this._clearNotice();
    if (this.selectedItems.length >= this.currentSize) return;

    const select = card.querySelector('.kits__variant-select');
    let variantId, price, image, variantTitle = '';

    if (select.tagName === 'SELECT') {
      const opt = select.options[select.selectedIndex];
      variantId = opt.value;
      price = parseInt(opt.dataset.price) || 0;
      image = opt.dataset.image || card.dataset.productImage;
      variantTitle = opt.value ? opt.text.replace(/\s*\(Esgotado\)\s*$/i, '').trim() : '';
    } else {
      variantId = select.value;
      price = parseInt(select.dataset.price) || 0;
      image = card.dataset.productImage;
    }

    const variantData = this._variantData(card, variantId);
    const alreadyInKit = this.selectedItems.filter(i => i.variantId == variantId).length;
    const max = this._maxQty(variantData);
    if (alreadyInKit + 1 > max) {
      const label = variantTitle ? `${card.dataset.productTitle} (${variantTitle})` : card.dataset.productTitle;
      this._showNotice(max <= 0
        ? `${label} está esgotado. Escolha outra variante.`
        : `${label} tem apenas ${max} ${max > 1 ? 'unidades' : 'unidade'} em estoque — você já adicionou o máximo disponível.`);
      card.classList.add('kits__card--unavailable');
      return;
    }

    const item = {
      uid: Date.now() + Math.random(),
      variantId,
      price,
      title: card.dataset.productTitle,
      variantTitle,
      image,
      productId: card.dataset.productId,
    };

    this.selectedItems.push(item);
    card.classList.add('kits__card--selected');

    this._renderSlots();
    this._updatePrices();
    this._updateActionBtn();
    this._updatePopup();
  }

  _removeItem(uid) {
    const item = this.selectedItems.find(i => i.uid === uid);
    this.selectedItems = this.selectedItems.filter(i => i.uid !== uid);
    this._clearNotice();

    // Remove selected class if no more items from this product
    if (item) {
      const stillSelected = this.selectedItems.some(i => i.productId === item.productId);
      if (!stillSelected) {
        const card = this.container.querySelector(`.kits__card[data-product-id="${item.productId}"]`);
        if (card) card.classList.remove('kits__card--selected');
      }
    }

    this._renderSlots();
    this._updatePrices();
    this._updateActionBtn();
    this._updatePopup();
  }

  _handleSizeChange(radio) {
    this.freeMode = radio.dataset.free === 'true';
    this.currentDiscount = parseFloat(radio.dataset.discount) || 0;

    if (this.freeMode) {
      this.currentSize = Infinity;
      this.freeMin = parseInt(radio.dataset.freeMin) || 3;
    } else {
      this.currentSize = parseInt(radio.value);
      this.freeMin = parseInt(this.container.dataset.freeMin) || 3;

      if (this.selectedItems.length > this.currentSize) {
        const removed = this.selectedItems.splice(this.currentSize);
        removed.forEach(item => {
          const stillSelected = this.selectedItems.some(i => i.productId === item.productId);
          if (!stillSelected) {
            const card = this.container.querySelector(`.kits__card[data-product-id="${item.productId}"]`);
            if (card) card.classList.remove('kits__card--selected');
          }
        });
      }
    }

    this._renderSlots();
    this._updatePrices();
    this._updateActionBtn();
    this._updatePopup();
  }

  _updateCardImage(card) {
    const select = card.querySelector('.kits__variant-select');
    if (!select || select.tagName !== 'SELECT') return;
    const opt = select.options[select.selectedIndex];
    const image = opt?.dataset.image || (select.value && JSON.parse(card.dataset.productVariants || '[]').find(v => v.id == select.value)?.image);
    if (!image) return;
    const img = card.querySelector('.kits__card-img');
    if (!img) return;
    img.src = image;
  }

  _updateSwatchDot(card) {
    const dot = card.querySelector('.kits__swatch-dot');
    if (!dot) return;
    const select = card.querySelector('.kits__variant-select');
    if (!select || select.tagName !== 'SELECT') return;
    const opt = select.options[select.selectedIndex];
    const swatch = opt?.dataset.swatch;
    if (swatch) {
      dot.style.background = swatch;
      dot.style.backgroundSize = 'cover';
      dot.style.backgroundPosition = 'center';
      dot.style.display = 'block';
    } else {
      dot.style.display = 'none';
      select.classList.remove('kits__select--swatch');
    }
  }

  _initPopup() {
    const panel = this.container.querySelector('.kits__panel');
    if (!panel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
          this.popupEl.classList.add('kits__mobile-popup--visible');
          this.popupEl.setAttribute('aria-hidden', 'false');
        } else {
          this.popupEl.classList.remove('kits__mobile-popup--visible');
          this.popupEl.setAttribute('aria-hidden', 'true');
        }
      },
      { threshold: 0 }
    );
    observer.observe(panel);
  }

  _updatePopup() {
    if (!this.popupEl) return;
    const count = this.selectedItems.length;
    const countEl = this.popupEl.querySelector('.kits__mobile-popup-count');
    const priceEl = this.popupEl.querySelector('.kits__mobile-popup-price');
    const addText = this.container.dataset.addToCartText || 'ADICIONAR AO CARRINHO';
    const pickText = this.container.dataset.pickText || 'ESCOLHA OS PRODUTOS';

    const isReady = this.freeMode
      ? count >= this.freeMin
      : count >= this.currentSize;

    if (this.popupBtn) {
      if (isReady) {
        this.popupBtn.textContent = addText;
        this.popupBtn.classList.add('kits__mobile-popup-btn--ready');
      } else {
        const remaining = this.freeMode
          ? this.freeMin - count
          : this.currentSize - count;
        this.popupBtn.textContent = count === 0
          ? pickText
          : `+${remaining} PRODUTO${remaining > 1 ? 'S' : ''}`;
        this.popupBtn.classList.remove('kits__mobile-popup-btn--ready');
      }
    }

    if (countEl) {
      if (count === 0) {
        countEl.textContent = pickText;
      } else if (count === 1) {
        countEl.textContent = '1 item selecionado';
      } else {
        countEl.textContent = `${count} itens selecionados`;
      }
    }

    if (priceEl && this.enableDiscount) {
      const total = this.selectedItems.reduce((s, i) => s + i.price, 0);
      if (total > 0 && this.currentDiscount > 0) {
        const discounted = total * (1 - this.currentDiscount / 100);
        priceEl.innerHTML = `<s>${this._money(total)}</s> ${this._money(discounted)}`;
      } else if (total > 0) {
        priceEl.textContent = this._money(total);
      } else {
        priceEl.textContent = '';
      }
    }
  }

  _jarSVG() {
    return `<svg class="kits__slot-jar" viewBox="0 0 60 78" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="30" cy="16" rx="22" ry="8" stroke="currentColor" stroke-width="1.4" stroke-dasharray="4.5 3"/>
      <path d="M8 16 L8 62 Q30 72 52 62 L52 16" stroke="currentColor" stroke-width="1.4" stroke-dasharray="4.5 3" stroke-linecap="round" fill="none"/>
    </svg>`;
  }

  _renderSlots() {
    if (!this.slotsEl) return;
    this.slotsEl.innerHTML = '';

    if (this.freeMode) {
      this.selectedItems.forEach(item => {
        const slot = document.createElement('div');
        slot.className = 'kits__slot kits__slot--filled';
        slot.innerHTML = `
          <button class="kits__slot-remove" aria-label="Remover">×</button>
          <img src="${item.image}" alt="${item.title}" loading="lazy">
        `;
        slot.querySelector('.kits__slot-remove').addEventListener('click', () => this._removeItem(item.uid));
        this.slotsEl.appendChild(slot);
      });
      const empty = document.createElement('div');
      empty.className = 'kits__slot kits__slot--empty';
      empty.innerHTML = this._jarSVG();
      this.slotsEl.appendChild(empty);
    } else {
      for (let i = 0; i < this.currentSize; i++) {
        const item = this.selectedItems[i];
        const slot = document.createElement('div');
        slot.className = `kits__slot ${item ? 'kits__slot--filled' : 'kits__slot--empty'}`;
        if (item) {
          slot.innerHTML = `
            <button class="kits__slot-remove" aria-label="Remover">×</button>
            <img src="${item.image}" alt="${item.title}" loading="lazy">
          `;
          slot.querySelector('.kits__slot-remove').addEventListener('click', () => this._removeItem(item.uid));
        } else {
          slot.innerHTML = this._jarSVG();
        }
        this.slotsEl.appendChild(slot);
      }
    }
  }

  _updatePrices() {
    if (!this.enableDiscount) return;

    const total = this.selectedItems.reduce((s, i) => s + i.price, 0);

    this.container.querySelectorAll(`input[name="kit-size-${this.sectionId}"]`).forEach(radio => {
      const size = parseInt(radio.value);
      const discount = parseFloat(radio.dataset.discount) || 0;
      const priceEl = radio.closest('.kits__size-option').querySelector('.kits__size-price');
      if (!priceEl) return;

      if (this.selectedItems.length === size) {
        const discounted = total * (1 - discount / 100);
        if (discount > 0) {
          priceEl.innerHTML = `<s>${this._money(total)}</s> <strong>${this._money(discounted)}</strong>`;
        } else {
          priceEl.innerHTML = `<strong>${this._money(total)}</strong>`;
        }
      } else {
        priceEl.innerHTML = '';
      }
    });
  }

  _money(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  _updateActionBtn() {
    if (!this.actionBtn) return;
    const pickText = this.container.dataset.pickText || 'ESCOLHA OS PRODUTOS';
    const addText = this.container.dataset.addToCartText || 'ADICIONAR AO CARRINHO';
    const count = this.selectedItems.length;

    if (this.freeMode) {
      const remaining = this.freeMin - count;
      if (remaining > 0) {
        this.actionBtn.textContent = count === 0
          ? `${pickText} (mín. ${this.freeMin})`
          : `ADICIONE MAIS ${remaining} PRODUTO${remaining > 1 ? 'S' : ''}`;
        this.actionBtn.classList.remove('kits__action-btn--ready');
      } else {
        this.actionBtn.textContent = `${addText} (${count} iten${count > 1 ? 's' : ''})`;
        this.actionBtn.classList.add('kits__action-btn--ready');
      }
    } else {
      const remaining = this.currentSize - count;
      if (remaining > 0) {
        this.actionBtn.textContent = remaining === this.currentSize
          ? pickText
          : `ESCOLHA MAIS ${remaining} PRODUTO${remaining > 1 ? 'S' : ''}`;
        this.actionBtn.classList.remove('kits__action-btn--ready');
      } else {
        this.actionBtn.textContent = addText;
        this.actionBtn.classList.add('kits__action-btn--ready');
      }
    }
  }

  _showNotice(msg) {
    if (!this.noticeEl) return;
    this.noticeEl.textContent = msg;
    this.noticeEl.hidden = false;
  }

  _clearNotice() {
    if (this.noticeEl) {
      this.noticeEl.hidden = true;
      this.noticeEl.textContent = '';
    }
    this.container.querySelectorAll('.kits__card--unavailable').forEach(c => c.classList.remove('kits__card--unavailable'));
  }

  _variantData(card, variantId) {
    if (!card) return null;
    try {
      return JSON.parse(card.dataset.productVariants || '[]').find(v => v.id == variantId) || null;
    } catch (e) {
      return null;
    }
  }

  _maxQty(v) {
    if (!v) return Infinity;
    if (v.available === false) return 0;
    if (!v.managed) return Infinity;
    if (v.policy === 'continue') return Infinity;
    return typeof v.inv === 'number' ? v.inv : Infinity;
  }

  _itemLabel(item) {
    return item.variantTitle ? `${item.title} (${item.variantTitle})` : item.title;
  }

  _findStockProblems() {
    const counts = {};
    this.selectedItems.forEach(i => { counts[i.variantId] = (counts[i.variantId] || 0) + 1; });

    const problems = [];
    Object.keys(counts).forEach(vid => {
      const item = this.selectedItems.find(i => i.variantId == vid);
      const card = this.container.querySelector(`.kits__card[data-product-id="${item.productId}"]`);
      const max = this._maxQty(this._variantData(card, vid));
      if (counts[vid] > max) {
        problems.push({ item, card, max });
      }
    });
    return problems;
  }

  async _addToCart() {
    const minRequired = this.freeMode ? this.freeMin : this.currentSize;
    if (this.selectedItems.length < minRequired) return;

    this._clearNotice();

    const problems = this._findStockProblems();
    if (problems.length) {
      problems.forEach(p => { if (p.card) p.card.classList.add('kits__card--unavailable'); });
      const msgs = problems.map(p => {
        const label = this._itemLabel(p.item);
        return p.max <= 0 ? `${label} (esgotado)` : `${label} (só ${p.max} em estoque)`;
      });
      this._showNotice(`Estoque insuficiente: ${msgs.join('; ')}. Ajuste as variantes para continuar.`);
      return;
    }

    this.actionBtn.disabled = true;
    this.actionBtn.textContent = 'Adicionando...';

    try {
      const items = this.selectedItems.map(item => ({
        id: parseInt(item.variantId),
        quantity: 1,
        properties: { _kit: this.sectionId }
      }));

      const sectionsToRender = ['cart-icon-bubble', 'cart-live-region-text', 'cart-notification-button', 'cart-drawer'];
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ items, sections: sectionsToRender, sections_url: window.location.pathname }),
      });

      if (res.ok) {
        this.actionBtn.textContent = '✓ Adicionado!';

        const cartType = (this.container.dataset.cartType || '').toLowerCase();

        // If cart page is selected, redirect immediately
        if (cartType === 'page') {
          window.location.href = window.Shopify?.routes?.root
            ? window.Shopify.routes.root + 'cart'
            : '/cart';
          return;
        }

        const data = await res.clone().json().catch(() => null);
        if (data && data.sections) {
          Object.entries(data.sections).forEach(([sectionId, html]) => {
            if (!html) return;
            const target = document.getElementById(`shopify-section-${sectionId}`) || document.getElementById(sectionId);
            if (!target) return;
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const source = parsed.getElementById(`shopify-section-${sectionId}`) || parsed.getElementById(sectionId) || parsed.querySelector('.shopify-section');
            if (source) target.innerHTML = source.innerHTML;
          });
        }

        fetch('/cart.js').then(r => r.json()).then(cart => {
          document.querySelectorAll('.cart-count-bubble').forEach(el => {
            el.textContent = cart.item_count;
            el.removeAttribute('aria-hidden');
          });
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
        });

        // If cart drawer is selected, open it
        if (cartType === 'drawer') {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          } else if (drawer) {
            drawer.classList.add('active', 'is-open');
            drawer.setAttribute('open', '');
          }
        }

        setTimeout(() => {
          this.selectedItems = [];
          this.container.querySelectorAll('.kits__card--selected').forEach(c => c.classList.remove('kits__card--selected'));
          this._renderSlots();
          this._updatePrices();
          this._updateActionBtn();
          this._updatePopup();
          this.actionBtn.disabled = false;
        }, 1800);
      } else {
        const data = await res.clone().json().catch(() => null);
        const desc = data && (data.description || data.message);
        this._showNotice(
          desc
            ? `${desc} Escolha outra variante para continuar.`
            : 'Não foi possível adicionar. Verifique se algum item está esgotado e escolha outra variante.'
        );
        this.actionBtn.disabled = false;
        this._updateActionBtn();
        return;
      }
    } catch (e) {
      this._showNotice('Erro de conexão. Tente novamente em instantes.');
      this.actionBtn.disabled = false;
      this._updateActionBtn();
    }
  }
}

// Init all kit sections on page
function initKitSections(root = document) {
  root.querySelectorAll('.kits-section').forEach(el => new KitBuilder(el));
}

document.addEventListener('DOMContentLoaded', () => initKitSections());

document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('.kits-section');
  if (section) new KitBuilder(section);
});
